import React, { useState } from 'react';
import { MfaModal } from './MfaModal';
import './Login.css';

interface User {
  id: string;
  garminEmail: string;
  displayName: string;
  isAdmin: boolean;
}

interface LoginProps {
  onLoginSuccess: (token: string, user: User) => void;
}

export default function Login({ onLoginSuccess }: LoginProps) {
  const [garminEmail, setGarminEmail] = useState('');
  const [garminPassword, setGarminPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMfa, setShowMfa] = useState(false);
  const [isSubmittingMfa, setIsSubmittingMfa] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ garminEmail, garminPassword }),
      });

      const data = await response.json();

      // Any auth failure: show the error inline by the fields and stay put.
      if (!response.ok) {
        setError(data.error || data.message || 'Sign-in failed. Please try again.');
        return;
      }

      if (data.needsMfa) {
        setShowMfa(true);
        return;
      }

      if (data.success && data.token) {
        localStorage.setItem('authToken', data.token);
        onLoginSuccess(data.token, data.user);
      } else {
        setError('Unexpected response from the server. Please try again.');
      }
    } catch (_err) {
      setError("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (mfaCode: string): Promise<void> => {
    setIsSubmittingMfa(true);
    try {
      const response = await fetch('/api/auth/mfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ garminEmail, garminPassword, mfaCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'MFA verification failed');
      }

      if (data.success && data.token) {
        localStorage.setItem('authToken', data.token);
        setShowMfa(false);
        onLoginSuccess(data.token, data.user);
      }
    } finally {
      setIsSubmittingMfa(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">FitSync</h1>
        <p className="login-subtitle">Sign in with your Garmin Connect account</p>

        <form onSubmit={handleSubmit} className="login-form">
          <input
            type="email"
            value={garminEmail}
            onChange={(e) => setGarminEmail(e.target.value)}
            placeholder="Garmin email"
            className="login-input"
            disabled={loading}
            autoFocus
          />

          <input
            type="password"
            value={garminPassword}
            onChange={(e) => setGarminPassword(e.target.value)}
            placeholder="Garmin password"
            className="login-input"
            disabled={loading}
          />

          {error && <div className="login-error">{error}</div>}

          <button type="submit" className="login-button" disabled={loading || !garminEmail || !garminPassword}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>

      {showMfa && (
        <MfaModal
          isOpen={showMfa}
          onClose={() => setShowMfa(false)}
          onSubmit={handleMfaSubmit}
          isLoading={isSubmittingMfa}
        />
      )}
    </div>
  );
}
